import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { buildPublicAssetUrl } from "@/lib/appPaths";
import { resetPasswordWithToken, validatePasswordResetToken } from "@/lib/authApi";
import { localizeAuthMessage } from "@/lib/authMessages";
import { Lock } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";

type TokenStatus = "checking" | "valid" | "invalid";

export default function ResetPassword() {
  const { t, language } = useLanguage();
  const token = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    return search.get("token")?.trim() || "";
  }, []);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>(token ? "checking" : "invalid");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bgRef = useRef<HTMLDivElement>(null);
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const blob3Ref = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef([
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ]);
  const rafRef = useRef<number | null>(null);

  const blobs = [
    { ref: blob1Ref, strength: 60,  ease: 0.06 },
    { ref: blob2Ref, strength: -90, ease: 0.04 },
    { ref: blob3Ref, strength: 45,  ease: 0.08 },
  ];

  useEffect(() => {
    const animate = () => {
      blobs.forEach(({ ref, strength, ease }, i) => {
        const cur = currentRef.current[i];
        const tgt = targetRef.current;
        cur.x += (tgt.x * strength - cur.x) * ease;
        cur.y += (tgt.y * strength - cur.y) * ease;
        if (ref.current) {
          ref.current.style.transform = `translate(${cur.x}px, ${cur.y}px)`;
        }
      });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const el = bgRef.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    targetRef.current = {
      x: (e.clientX - left) / width - 0.5,
      y: (e.clientY - top) / height - 0.5,
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setErrorMessage(t("重置链接缺少 token 参数。", "The reset link is missing the token."));
      setTokenStatus("invalid");
      return;
    }

    let active = true;
    setTokenStatus("checking");
    setErrorMessage("");

    validatePasswordResetToken(token)
      .then(result => {
        if (!active) return;
        if (!result.ok || !result.valid) {
          setTokenStatus("invalid");
          setErrorMessage(
            localizeAuthMessage(
              language,
              t("重置链接无效或已过期。", "The reset link is invalid or has expired."),
              result.message
            )
          );
          return;
        }
        setTokenStatus("valid");
      })
      .catch(error => {
        if (!active) return;
        console.error("Validate reset token failed:", error);
        setTokenStatus("invalid");
        setErrorMessage(t("令牌校验失败，请稍后重试。", "Token validation failed. Please try again."));
      });

    return () => {
      active = false;
    };
  }, [language, t, token]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (tokenStatus !== "valid" || !password.trim() || !confirmPassword.trim() || isSubmitting) {
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(t("两次输入的密码不一致", "Passwords do not match."));
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      const result = await resetPasswordWithToken({ token, newPassword: password });

      if (!result.ok || !result.reset) {
        setErrorMessage(
          localizeAuthMessage(
            language,
            t("密码重置失败，请稍后重试。", "Password reset failed. Please try again."),
            result.message
          )
        );
        return;
      }

      setSuccessMessage(
        localizeAuthMessage(
          language,
          t("密码已重置，请返回登录。", "Password reset complete. Please sign in."),
          result.message
        )
      );
      setTokenStatus("invalid");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = tokenStatus === "valid";

  const subtitle = tokenStatus === "checking"
    ? t("正在验证重置链接...", "Validating reset link...")
    : successMessage
    ? t("密码已重置成功。", "Your password has been reset.")
    : t("设置一个新的登录密码。", "Set a new password for your account.");

  return (
    <div
      ref={bgRef}
      className="fixed inset-0 flex items-center justify-center overflow-y-auto"
      onMouseMove={handleMouseMove}
      style={{ backgroundColor: "#f4f9f4" }}
    >
      <style>{`
        @keyframes tm-breathe-1 { 0%,100% { opacity:1; } 50% { opacity:0.45; } }
        @keyframes tm-breathe-2 { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
        @keyframes tm-breathe-3 { 0%,100% { opacity:1; } 50% { opacity:0.50; } }
      `}</style>
      {/* 光晕1：左上 */}
      <div ref={blob1Ref} style={{
        position: "absolute", left: "12%", top: "25%", width: "65%", height: "55%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(96,175,106,0.18) 0%, transparent 70%)",
        animation: "tm-breathe-1 2s ease-in-out infinite",
      }} />
      {/* 光晕2：右上 */}
      <div ref={blob2Ref} style={{
        position: "absolute", right: "12%", top: "12%", width: "60%", height: "70%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(140,205,145,0.14) 0%, transparent 65%)",
        animation: "tm-breathe-2 2.5s ease-in-out infinite 0.4s",
      }} />
      {/* 光晕3：底部 */}
      <div ref={blob3Ref} style={{
        position: "absolute", left: "15%", bottom: "-10%", width: "80%", height: "55%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(110,188,118,0.12) 0%, transparent 70%)",
        animation: "tm-breathe-3 1.8s ease-in-out infinite 0.2s",
      }} />

      <div className="w-full max-w-[360px] px-4 flex flex-col items-center gap-7 py-10 mb-32">
        {/* Logo + Title */}
        <div className="flex flex-col items-center gap-3">
          <img
            src={buildPublicAssetUrl("/logo_character.png")}
            alt="TeachMaster"
            className="w-24 object-contain"
          />
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-medium text-foreground">
              {t("重置密码", "Reset Password")}
            </h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              id="reset-password"
              type="password"
              placeholder={t("新密码（至少 6 位）", "New password (at least 6 characters)")}
              value={password}
              onChange={event => {
                setPassword(event.target.value);
                if (errorMessage) setErrorMessage("");
              }}
              className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
              disabled={!isValid || isSubmitting}
            />
          </div>

          <div className="relative">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              id="reset-password-confirm"
              type="password"
              placeholder={t("再次输入新密码", "Enter new password again")}
              value={confirmPassword}
              onChange={event => {
                setConfirmPassword(event.target.value);
                if (errorMessage) setErrorMessage("");
              }}
              className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
              disabled={!isValid || isSubmitting}
            />
          </div>

          {errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {successMessage && (
            <Alert>
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="h-11 w-full rounded-xl mt-1 shadow-sm"
            disabled={!isValid || !password.trim() || !confirmPassword.trim() || isSubmitting}
          >
            {isSubmitting ? t("提交中...", "Resetting...") : t("确认重置密码", "Reset Password")}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground">
          {t("想起密码了？", "Remember your password?")}{" "}
          <Link href="/login" className="text-foreground font-medium hover:underline transition-colors">
            {t("返回登录", "Back to sign in")}
          </Link>
        </p>
      </div>
    </div>
  );
}
